import { BRIGHT_COLORS } from '~/constants/color';
import { imageHoverState } from '~/pages/projectDetails/civildraw/widgets/ObjectDetails/ImagePreview/ImageHoverPreview';
import type { ImageObject as MapImageObject } from '../../../../common/types';
import * as ImageCache from '../../ImageCache';
import CanvasObject from '../CanvasObject';
import * as geometry from '../common/geometry';
import type SurfaceBase from '../SurfaceBase';
import type { ComputedPath, LngLat, Point } from '../SurfaceBase';
import { HOVER_DELAY, SVG_WIDTH, svgPath } from './common';

export default class ImageObject extends CanvasObject {
  image: MapImageObject;
  imageCanvas: CanvasImageSource | null = null;
  firstCoordinate: LngLat;
  computedPath: ComputedPath;
  minimizedIcon: Path2D = new Path2D(svgPath);
  minimizedHitCoords: {
    point: Point;
    width: number;
    height: number;
  } = { point: { x: NaN, y: NaN }, width: 0, height: 0 };
  iconCenter: Point = { x: NaN, y: NaN };
  hoverTimeout: NodeJS.Timeout | null = null;

  constructor(id: string, surface: SurfaceBase, image: MapImageObject) {
    super(id, surface);
    this.image = image;

    ImageCache.getImage(image.files![0].path).then((imageCanvas) => {
      this.imageCanvas = imageCanvas;
      this.renderAll();
    });
    this.firstCoordinate = {
      lng: image.geometry.coordinates[0][0][0],
      lat: image.geometry.coordinates[0][0][1],
    };
    this.computedPath = surface.getPath(image.geometry.coordinates);

    this.on('hoverStart', (e) => {
      if (this.image.properties.minimized) {
        this.hoverTimeout = setTimeout(() => {
          const file = this.image.files![0];
          const lngLat = this.surface.toMapCoords(this.iconCenter);
          imageHoverState.setParams(file, this.iconCenter, lngLat);
        }, HOVER_DELAY);
      }
    });

    this.on('hoverEnd', (e) => {
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = null;
        imageHoverState.reset();
      }
    });
  }

  renderIcon = (ctx: CanvasRenderingContext2D) => {
    const { properties, title, geometry } = this.image;
    const [lng, lat] = geometry.coordinates[0][3];
    const p = this.surface.toScreenCoords({ lng, lat });

    const x = p.x + SVG_WIDTH / 2;
    const y = p.y - SVG_WIDTH;

    this.minimizedHitCoords = {
      point: { x: p.x, y },
      width: SVG_WIDTH,
      height: SVG_WIDTH,
    };

    this.iconCenter = { x: p.x + SVG_WIDTH / 2, y: p.y - SVG_WIDTH / 2 };

    ctx.save();
    ctx.translate(p.x, y);
    ctx.fillStyle = properties.color || 'red';
    ctx.fill(this.minimizedIcon);
    ctx.restore();

    if (properties.showTitle) {
      const padding = 10;
      const fontSize = 14;
      const labelOffset = padding;

      ctx.save();
      ctx.font = `bold ${fontSize}px arial`;
      const textWidth = ctx.measureText(title).width;

      const pt = {
        x: x - textWidth / 2 - padding / 2,
        y: y - labelOffset - fontSize - padding,
      };

      const w = textWidth + padding;
      const h = fontSize + padding;

      ctx.fillStyle = properties.color || 'red';
      ctx.beginPath();
      ctx.roundRect(pt.x, pt.y, w, h, [5]);
      ctx.fill();

      ctx.textBaseline = 'top';
      ctx.textAlign = 'left';
      ctx.fillStyle = BRIGHT_COLORS.includes(properties.color) ? '#000000' : '#FFFFFF';

      ctx.fillText(title, pt.x + padding / 2, pt.y + padding / 2);
      ctx.restore();
    }
  };

  render(ctx: CanvasRenderingContext2D) {
    if (this.image.properties.minimized) {
      this.renderIcon(ctx);
      return;
    }

    if (this.imageCanvas) {
      const { properties } = this.image;
      // image
      const [p1, p2, p3, p4] = this.image.geometry.coordinates[0]
        .slice(0, -1)
        .map(([lng, lat]) => this.surface.toScreenCoords({ lng, lat }));

      const width = geometry.distance(p1, p2);
      const height = geometry.distance(p2, p3);
      const angleInRadians = geometry.lineAngle(p1, p2);
      const center = geometry.polygonCentroid([p1, p2, p3, p4]);

      ctx.save();
      ctx.translate(center.x, center.y);
      ctx.rotate(angleInRadians);
      ctx.translate(-center.x, -center.y);

      ctx.globalAlpha = properties.fillOpacity;
      ctx.drawImage(this.imageCanvas, center.x - width / 2, center.y - height / 2, width, height);
      ctx.restore();

      // border
      if (!properties.showBorder) {
        return;
      }

      const scale = 2 ** (this.surface.map.getZoom() - this.computedPath.z);
      const { x, y } = this.surface.toScreenCoords(this.firstCoordinate);
      const isDashed = properties.strokeStyle === 'dashed';
      ctx.save();
      ctx.globalAlpha = properties.strokeOpacity ?? 1;
      isDashed && ctx.setLineDash([10, 4]);
      ctx.strokeStyle = properties.color ?? '#000000';
      ctx.lineWidth = (properties.strokeWidth ?? 4) / scale;
      ctx.translate(x, y);
      ctx.scale(scale, scale);
      ctx.stroke(this.computedPath.path);
      ctx.restore();
      ctx.globalAlpha = 1;
      ctx.fillStyle = properties.color;
      this.surface.resetCanvasTransform(ctx);
      ctx.restore();
    } else {
      const [p1, p2, p3, p4] = this.image.geometry.coordinates[0]
        .slice(0, -1)
        .map(([lng, lat]) => this.surface.toScreenCoords({ lng, lat }));
      const center = geometry.polygonCentroid([p1, p2, p3, p4]);
      ctx.font = '16px arial';
      ctx.fillText('Loading...', center.x - 24, center.y);
    }
  }

  drawHitArea(ctx: OffscreenCanvasRenderingContext2D, color: string): void {
    if (this.image.properties.minimized) {
      const { point, width, height } = this.minimizedHitCoords;
      ctx.save();
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.fillRect(point.x, point.y, width, height);
      ctx.restore();
      return;
    }
    const [p1, p2, p3, p4] = this.image.geometry.coordinates[0]
      .slice(0, -1)
      .map(([lng, lat]) => this.surface.toScreenCoords({ lng, lat }));

    ctx.beginPath();
    ctx.fillStyle = color;

    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    ctx.lineTo(p3.x, p3.y);
    ctx.lineTo(p4.x, p4.y);
    ctx.fill();
  }
}
